import { createSelector } from '@reduxjs/toolkit';
import {
  getLongestRRSelector,
  millisecondsIntervalSelector,
  pQrsWaveListSelector,
  pWaveListSelector,
  qrsTWaveListSelector,
  qrsWaveListSelector,
} from '../../wave/selectors/wave.selectors';
import {
  frequencySelector,
  leadDataSelector,
} from '../../ecg/selectors/signal.selectors';
import {
  exactDurationSelector,
  sexSelector,
} from '../../ecg/selectors/ecg.selectors';
import { median } from '../../utils/math';
import { getWaveDuration, isWaveOfType } from '../../wave/utils/wave.util';
import { MINUTE } from '../../utils/time';
import {
  computeAxes,
  computePositiveNetDeflection,
} from '../utils/signal.utils';
import { Metric } from '../types/metric.type';
import { BPM, DEGREE, MS } from '../../layout/types/layout-state.type';
import { QTCR_QRS_DUR_THRES_MS } from '../utils/metric.utils';

export const hrSelector = createSelector(
  millisecondsIntervalSelector,
  (RRs) => {
    if (RRs.length === 0) {
      return;
    }
    const meanRR = RRs.reduce((sum, RR) => sum + RR.duration, 0) / RRs.length;
    const hr = MINUTE / meanRR;
    return Math.round(hr);
  },
);

export const svQrsDurationListSelector = createSelector(
  qrsWaveListSelector,
  (waves) => {
    const filteredWaves = waves.filter(({ tags }) =>
      ['aberration', 'ectopic'].every((tag) => !tags.includes(tag)),
    );
    if (filteredWaves.length === 0) {
      return;
    }
    const durations = filteredWaves.map((wave) => getWaveDuration(wave));
    return durations;
  },
);

export const svQrsDurationSelector = createSelector(
  svQrsDurationListSelector,
  (durations) => {
    if (durations == null) {
      return;
    }
    const med = median(durations);
    return med && Math.round(med);
  },
);

export const qrsDurationListSelector = createSelector(
  qrsWaveListSelector,
  (waves) => {
    if (waves.length === 0) {
      return;
    }
    const durations = waves.map((wave) => getWaveDuration(wave));
    return durations;
  },
);

export const qrsDurationSelector = createSelector(
  qrsDurationListSelector,
  (durations) => {
    if (durations == null) {
      return;
    }
    const med = median(durations);
    return med && Math.round(med);
  },
);

export const sinusPDurationListSelector = createSelector(
  pWaveListSelector,
  (waves) => {
    const filteredWaves = waves.filter(({ tags }) => tags.includes('sinus'));
    if (filteredWaves.length === 0) {
      return;
    }
    const durations = filteredWaves.map((wave) => getWaveDuration(wave));
    return durations;
  },
);

export const sinusPDurationSelector = createSelector(
  sinusPDurationListSelector,
  (durations) => {
    if (durations == null) {
      return;
    }
    const med = median(durations);
    return med && Math.round(med);
  },
);

export const pDurationListSelector = createSelector(
  pWaveListSelector,
  (waves) => {
    if (waves.length === 0) {
      return;
    }
    const durations = waves.map((wave) => getWaveDuration(wave));
    return durations;
  },
);

export const pDurationSelector = createSelector(
  pDurationListSelector,
  (durations) => {
    if (durations == null) {
      return;
    }
    const med = median(durations);
    return med && Math.round(med);
  },
);

// only use for CL_p_1 RAE rule that take only the positive deflection
export const pAmplitudesListSelector = createSelector(
  pWaveListSelector,
  leadDataSelector,
  frequencySelector,
  exactDurationSelector,
  (waves, leads, frequency, ecgDuration) => {
    const pWaves = waves.filter(
      (wave) => wave.onset >= 0 && wave.offset <= ecgDuration,
    );
    if (pWaves.length === 0) {
      return;
    }

    const amplitudes = pWaves
      .map((wave) => {
        if (!wave.tags.includes('sinus')) {
          return null;
        }
        const mliiNetDeflection = computePositiveNetDeflection(
          wave,
          leads['MLII'],
          frequency,
        );
        const v1NetDeflection = computePositiveNetDeflection(
          wave,
          leads['V1'],
          frequency,
        );
        const v2NetDeflection = computePositiveNetDeflection(
          wave,
          leads['V2'],
          frequency,
        );
        return { mliiNetDeflection, v1NetDeflection, v2NetDeflection };
      })
      .filter((amplitude) => amplitude != null);

    return amplitudes;
  },
);

// only use for CL_p_1 RAE rule that take only the positive deflection
export const pAmplitudeSelector = createSelector(
  pAmplitudesListSelector,
  (amplitudes) => {
    if (amplitudes == null) {
      return;
    }

    const mliiAmplitudes: number[] = [];
    const v1Amplitudes: number[] = [];
    const v2Amplitudes: number[] = [];

    amplitudes.forEach((amplitude) => {
      if (amplitude == null) {
        return;
      }
      const { mliiNetDeflection, v1NetDeflection, v2NetDeflection } = amplitude;
      mliiAmplitudes.push(mliiNetDeflection);
      v1Amplitudes.push(v1NetDeflection);
      v2Amplitudes.push(v2NetDeflection);
    });

    if (
      mliiAmplitudes.length === 0 &&
      v1Amplitudes.length === 0 &&
      v2Amplitudes.length === 0
    ) {
      return;
    }

    return {
      MLII: median(mliiAmplitudes),
      V1: median(v1Amplitudes),
      V2: median(v2Amplitudes),
    };
  },
);

export const prIntervalListSelector = createSelector(
  pQrsWaveListSelector,
  (pQrsWaves) => {
    if (pQrsWaves.length < 2) {
      return;
    }

    const intervals = pQrsWaves
      .map((wave, index, pQrsWaves) => {
        // keep only P sinus waves conducted
        if (
          !isWaveOfType(wave, 'P') ||
          !wave.tags.includes('sinus') ||
          wave.tags.includes('non-conducted')
        ) {
          return null;
        }

        // keep only QRS sinus not noisy_RR
        const nextWave = pQrsWaves[index + 1];
        if (
          nextWave == null ||
          !isWaveOfType(nextWave, 'QRS') ||
          !nextWave.tags.includes('sinus') ||
          nextWave.tags.includes('noisy_rr')
        ) {
          return null;
        }
        // at this point we are sure that :
        //  - wave is a P sinus
        //  - nextWave is a QRS sinus not noisy_RR

        return nextWave.onset - wave.onset;
      })
      .filter((interval): interval is number => interval != null);

    if (intervals.length === 0) {
      return;
    }

    return intervals;
  },
);

export const minPrIntervalSelector = createSelector(
  prIntervalListSelector,
  (intervals) => {
    if (intervals == null) {
      return;
    }

    return Math.round(Math.min(...intervals));
  },
);

export const qtIntervalListSelector = createSelector(
  qrsTWaveListSelector,
  (qrsTWaves) => {
    if (qrsTWaves.length < 2) {
      return;
    }

    const intervals = qrsTWaves
      .map((wave, index, qrsTWaves) => {
        if (
          !isWaveOfType(wave, 'QRS') ||
          wave.tags.includes('aberration') ||
          wave.tags.includes('ectopic')
        ) {
          return null;
        }
        const nextWave = qrsTWaves[index + 1];
        if (nextWave == null || !isWaveOfType(nextWave, 'T')) {
          return null;
        }
        return nextWave.offset - wave.onset;
      })
      .filter((interval): interval is number => interval != null);

    if (intervals.length === 0) {
      return;
    }

    return intervals;
  },
);

export const qtIntervalSelector = createSelector(
  qtIntervalListSelector,
  (intervals) => {
    if (intervals == null) {
      return;
    }
    const med = median(intervals);
    return med && Math.round(med);
  },
);

export const qtcbIntervalListSelector = createSelector(
  qrsTWaveListSelector,
  (qrsTWaves) => {
    if (qrsTWaves.length < 2) {
      return;
    }

    const intervals = qrsTWaves
      .map((wave, index, qrsTWaves) => {
        if (
          !isWaveOfType(wave, 'QRS') ||
          wave.tags.includes('aberration') ||
          wave.tags.includes('ectopic') ||
          wave.tags.includes('noisy_rr')
        ) {
          return null;
        }
        const nextWave = qrsTWaves[index + 1];
        if (nextWave == null || !isWaveOfType(nextWave, 'T')) {
          return null;
        }

        const prevWave = qrsTWaves[index - 2];
        if (prevWave == null || !isWaveOfType(prevWave, 'QRS')) {
          return null;
        }

        // Bazett method QTcB = QT / sqrt(RR) with RR in seconds
        const qtcb =
          (nextWave.offset - wave.onset) /
          Math.sqrt((wave.onset - prevWave.onset) / 1000);
        return Math.floor(qtcb);
      })
      .filter((interval): interval is number => interval != null);

    if (intervals.length === 0) {
      return;
    }
    return intervals;
  },
);

export const qtcbIntervalSelector = createSelector(
  qtcbIntervalListSelector,
  (intervals) => {
    if (intervals == null) {
      return;
    }
    const med = median(intervals);
    return med && Math.round(med);
  },
);

export const qtcrIntervalListSelector = createSelector(
  qrsTWaveListSelector,
  sexSelector,
  (qrsTWaves, sex) => {
    const intervals = qrsTWaves
      .map((wave, index, qrsTWaves) => {
        if (
          !isWaveOfType(wave, 'QRS') ||
          wave.tags.includes('aberration') ||
          wave.tags.includes('ectopic') ||
          wave.tags.includes('noisy_rr')
        ) {
          return null;
        }
        const nextWave = qrsTWaves[index + 1];
        if (nextWave == null || !isWaveOfType(nextWave, 'T')) {
          return null;
        }

        const prevWave = qrsTWaves[index - 2];
        if (prevWave == null || !isWaveOfType(prevWave, 'QRS')) {
          return null;
        }
        // QTcR = QT - 155 * (RR - 1) - 0.93 * (QRS - 139) + k
        // rr in second
        // QRS in millisecond
        // QT in millisecond
        // k depends on patient sex
        const QT = nextWave.offset - wave.onset;
        const RR = (wave.onset - prevWave.onset) / 1000;
        const qrsDuration = getWaveDuration(wave);
        const k = sex === 'F' ? -34 : -22;
        const qtcr = QT - 155 * (RR - 1) - 0.93 * (qrsDuration - 139) + k;

        return Math.floor(Math.max(qtcr, 0));
      })
      .filter((interval): interval is number => interval != null);

    if (intervals.length === 0) {
      return;
    }
    return intervals;
  },
);

export const qtcrIntervalSelector = createSelector(
  qtcrIntervalListSelector,
  (intervals) => {
    if (intervals == null) {
      return;
    }
    const med = median(intervals);
    return med && Math.round(med);
  },
);

export const qtcIntervalSelector = createSelector(
  svQrsDurationSelector,
  qtcbIntervalSelector,
  qtcrIntervalSelector,
  (svQrsDuration, qtcb, qtcr) => {
    if (svQrsDuration == null) {
      return;
    }

    return svQrsDuration >= QTCR_QRS_DUR_THRES_MS ? qtcr : qtcb;
  },
);

export const qrsAxisListSelector = createSelector(
  qrsWaveListSelector,
  leadDataSelector,
  frequencySelector,
  exactDurationSelector,
  (waves, leads, frequency, ecgDuration) => {
    if (waves.length === 0) {
      return;
    }

    const qrsWaves = waves.filter(
      (wave) =>
        wave.onset >= 0 &&
        wave.offset <= ecgDuration &&
        !wave.tags.includes('aberration') &&
        !wave.tags.includes('ectopic'),
    );

    if (qrsWaves.length === 0) {
      return;
    }
    const qrsAxes = computeAxes(qrsWaves, leads, frequency);

    return qrsAxes;
  },
);

export const qrsAxisSelector = createSelector(qrsAxisListSelector, (axes) => {
  if (axes == null) {
    return;
  }
  const med = median(axes);
  return med && Math.round(med);
});

export const pAxisListSelector = createSelector(
  pWaveListSelector,
  leadDataSelector,
  frequencySelector,
  exactDurationSelector,
  (waves, leads, frequency, ecgDuration) => {
    if (waves.length === 0) {
      return;
    }
    const pWaves = waves.filter(
      (wave) => wave.onset >= 0 && wave.offset <= ecgDuration,
    );
    if (pWaves.length === 0) {
      return;
    }
    const pAxes = computeAxes(pWaves, leads, frequency);

    return pAxes;
  },
);

export const pAxisSelector = createSelector(pAxisListSelector, (axes) => {
  if (axes == null) {
    return;
  }
  const med = median(axes);
  return med && Math.round(med);
});

export const computedMetricsSelector = createSelector(
  hrSelector,
  getLongestRRSelector,
  qrsDurationSelector,
  pDurationSelector,
  minPrIntervalSelector,
  qtIntervalSelector,
  qtcbIntervalSelector,
  qtcrIntervalSelector,
  qrsAxisSelector,
  pAxisSelector,
  svQrsDurationSelector,
  (
    hr,
    longestRR,
    qrsDuration,
    pDuration,
    minPrInterval,
    qtInterval,
    qtcb,
    qtcr,
    qrsAxis,
    pAxis,
    svQrsDuration,
  ) => {
    const metrics: Metric[] = [];
    if (hr != null) {
      metrics.push({
        description: 'HR',
        unit: BPM,
        value: hr,
        color: 'neutral.900',
      });
    }

    if (longestRR != null) {
      metrics.push({
        description: 'Longest RR',
        unit: MS,
        value: longestRR,
        color: 'neutral.900',
      });
    }
    if (qrsDuration != null) {
      metrics.push({
        description: 'QRS',
        unit: MS,
        value: qrsDuration,
        color: 'neutral.900',
      });
    }
    if (pDuration != null) {
      metrics.push({
        description: 'P',
        unit: MS,
        value: pDuration,
        color: 'neutral.900',
      });
    }
    if (minPrInterval != null) {
      metrics.push({
        description: 'min sinus PQ',
        unit: MS,
        value: minPrInterval,
        color: 'neutral.900',
      });
    }
    if (qtInterval != null) {
      metrics.push({
        description: 'QT',
        unit: MS,
        value: qtInterval,
        color: 'neutral.900',
      });
    }
    if (
      svQrsDuration != null &&
      svQrsDuration >= QTCR_QRS_DUR_THRES_MS &&
      qtcr != null
    ) {
      metrics.push({
        description: 'QTcR',
        unit: MS,
        value: qtcr,
        color: 'neutral.900',
      });
    } else if (
      svQrsDuration != null &&
      svQrsDuration < QTCR_QRS_DUR_THRES_MS &&
      qtcb != null
    ) {
      metrics.push({
        description: 'QTcB',
        unit: MS,
        value: qtcb,
        color: 'neutral.900',
      });
    }
    if (qrsAxis != null) {
      metrics.push({
        description: 'QRS axis',
        unit: DEGREE,
        value: qrsAxis,
        color: 'neutral.900',
      });
    }
    if (pAxis != null) {
      metrics.push({
        description: 'P axis',
        unit: DEGREE,
        value: pAxis,
        color: 'neutral.900',
      });
    }
    return metrics;
  },
);
